// ITEM 3c — THE MANUAL, HANDED OVER, IN THE CONVERSATION.
//
// HER CLAUSE 2: *"she offers and delivers the PDF right in the Ask Lala thread as a downloadable
// file — the member taps and has it — then directs them to the page."*
//
// Props in, tree out, no state and no hook — the entry requirement, so `scripts/manual-handoff.mjs`
// CALLS this and reads the tree back rather than matching its source. What that buys, which no regex
// over App.tsx could: the link is proved to carry the SERVER'S OWN url (never a path composed here,
// which would be a second answer to where the one canonical file lives), the `download` attribute is
// proved present so a tap really lands a file rather than opening a viewer, and **the page line is
// proved to appear only when the server sent a page** — a card that printed "page undefined", or
// worse a plausible number, is exactly the fabrication this whole item is built around.
import { FileText } from "lucide-react";
import { reportClientLoss } from "../lib/clientLoss";
// PDF-1: the ONE transport, so a document is SAVED rather than handed to another app.
import { downloadFromUrl } from "../lib/downloads";

// The shape the SERVER composes (`manualHandoff` in server/manual-pages.js). `page` is optional and
// its absence is a fact, not a default: a product that is not written up in the manual has no page,
// and nothing on this side may fill that in.
export type ManualHandoff = {
  title: string;
  filename: string;
  url: string;
  page?: number;
  product?: string;
};

export function ManualHandoffCard({ manual, onSave = downloadFromUrl }: { manual: ManualHandoff; onSave?: (url: string) => unknown }) {
  if (!manual?.url) return null;
  // A page is only ever a real page. `0`, `-1` and a string that is not a number are all "no page",
  // because the one thing this card may never do is print a number the file does not have.
  const page = Number(manual.page);
  const hasPage = Number.isFinite(page) && page >= 1;
  return (
    <div className="manual-handoff" aria-label="Lala's product manual">
      {/* PDF-1: the same repair as the card below it and the seals before that. A bare
          `<a href download target="_blank">` on a phone is a handoff to whatever app claims the
          type — the manual would open in a reader she did not choose and never reach her files. */}
      <button
        type="button"
        className="manual-handoff__file"
        onClick={() => { void onSave(manual.url); }}
      >
        <span className="manual-handoff__icon" aria-hidden="true"><FileText size={18} /></span>
        <span className="manual-handoff__text">
          <span className="manual-handoff__title">{manual.title}</span>
          <span className="manual-handoff__hint">Tap to save</span>
        </span>
      </button>
      {hasPage ? (
        <p className="manual-handoff__page">
          {manual.product ? `${manual.product} is on page ` : "Page "}
          <strong>{Math.trunc(page)}</strong>
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// PDF-1 — **THE MEMBER'S OWN WORK, HANDED OVER, IN THE SAME CONVERSATION.**
//
// It lives beside `ManualHandoffCard` rather than in its own file because they are the same act —
// *a real file handed to a member inside a thread* — and the two would otherwise drift into two
// answers about what that looks like (R28/R29). Props in, tree out, no state and no hook, so
// `scripts/pdf-compile.mjs` CALLS it rather than matching its source.
//
// **NO PAGE, NO COUNT AND NO TITLE IS COMPOSED HERE.** Every one of them is the server's, because
// this side cannot know how many pieces really reached the document — the door re-resolves her rows
// at download time and a reading she deleted since is simply not in it.

// The shape the SERVER composes. `pieces` are the titles that rode in it, in order.
export type Compilation = {
  title: string;
  filename: string;
  url: string;
  pieces?: string[];
};

/**
 * >>> **HER ORDER, 2026-09-18 — THE DOCUMENT PATH REPORTS ITS OWN LOSS (#278/#279).** <<<
 *
 * Two members: *"the put_in_a_document tool appears to be failing silently."* **The server is not
 * failing** — fourteen days of its own log, 44 of 44 and 129 of 129, not one failed write. So the
 * loss is between the payload leaving the house and the file reaching her, and this is the card she
 * taps.
 *
 * **THE REPORTER IS HANDED TO THE TRANSPORT RATHER THAN IMPORTED BY IT.** `src/lib/downloads.ts` is
 * one of the pure laws a `.mjs` gate loads and runs, and its entry requirement is that it imports
 * nothing but types. A component has no such rule.
 */
export function saveDocumentReporting(url: string) {
  return downloadFromUrl(url, undefined, { report: (kind, info) => reportClientLoss(kind, info) });
}

export function CompilationCard({ compilation, onSave = saveDocumentReporting }: { compilation: Compilation; onSave?: (url: string) => unknown }) {
  if (!compilation?.url) return null;
  const pieces = Array.isArray(compilation.pieces) ? compilation.pieces.filter(Boolean) : [];
  return (
    <div className="manual-handoff" aria-label="Your work as a document">
      {/* >>> **PDF-1 (her ruling, 2026-09-05): "the on-phone handoff fixed like the seals were,
          never a broken external link."** <<<
          This was `<a href download target="_blank">` with the words "Tap to download" on it — the
          SAME trap `ic318@aol.com` filed twice about her seals (IT #198/#199). On a phone that is
          not a download: iOS and Android largely ignore the `download` attribute, `target="_blank"`
          opens it in another tab, and the file goes to whatever app claims the type. She taps her
          own document and loses it to an app she did not ask for.
          The ONE transport instead — the same `downloadFromUrl` the seals were moved onto, which
          reads the route's own 401/404/500 (an anchor can read none of them) and takes the filename
          from `Content-Disposition` rather than guessing at it. */}
      <button
        type="button"
        className="manual-handoff__file"
        onClick={() => { void onSave(compilation.url); }}
      >
        <span className="manual-handoff__icon" aria-hidden="true"><FileText size={18} /></span>
        <span className="manual-handoff__text">
          <span className="manual-handoff__title">{compilation.title}</span>
          <span className="manual-handoff__hint">Tap to save</span>
        </span>
      </button>
      {pieces.length ? (
        <p className="manual-handoff__page">{pieces.join(" · ")}</p>
      ) : null}
    </div>
  );
}
